/**
 * Pure LinkedIn measurement helpers: which of an ad account's conversion rules
 * a new campaign should be attached to, and whether the campaign will be able
 * to report a lead at all.
 *
 * A LinkedIn campaign reports a conversion only when a conversion rule is
 * associated with it (`PUT /rest/campaignConversions`). Mora creates campaigns
 * as DRAFT and, before this module, never associated anything, so no Mora-built
 * campaign could count a lead. Reads of the rules themselves live in
 * `conversion-tracking.ts`; this file only decides.
 */

export const LINKEDIN_MAX_CONVERSION_IDS = 20;

export interface LinkedInMeasurementRule {
  conversionId: string;
  type: string | null;
  enabled: boolean | null;
}

const CONVERSION_ID = /^\d+$/;

/**
 * Enabled rules only, LEAD-type first, capped at 20. The order inside each
 * group follows the input order, and duplicate or non-numeric ids are dropped.
 */
export function defaultConversionIds(rules: readonly LinkedInMeasurementRule[]): string[] {
  const seen = new Set<string>();
  const enabled = rules.filter((rule) => {
    if (rule.enabled !== true || !CONVERSION_ID.test(rule.conversionId)) return false;
    if (seen.has(rule.conversionId)) return false;
    seen.add(rule.conversionId);
    return true;
  });
  // Lead rules only, when there are any: attaching every enabled rule also
  // counted unrelated actions (a PTO request, a download) as this campaign's
  // conversions. With no lead rule, every enabled rule is the only measure left.
  const lead = enabled.filter((rule) => rule.type === 'LEAD');
  const chosen = lead.length > 0 ? lead : enabled;
  return chosen.slice(0, LINKEDIN_MAX_CONVERSION_IDS).map((rule) => rule.conversionId);
}

export type LinkedInMeasurementState = 'ready' | 'no_rule' | 'rules_disabled';

export interface LinkedInMeasurementStatus {
  state: LinkedInMeasurementState;
  sentence: string;
}

export function linkedInMeasurementStatus(input: {
  objective: string | null | undefined;
  conversionIds: readonly string[];
  rules: readonly LinkedInMeasurementRule[];
}): LinkedInMeasurementStatus {
  const { objective, conversionIds, rules } = input;
  const wantsLeads = objective === 'LEAD_GENERATION' || objective === 'WEBSITE_CONVERSION';
  const subject = wantsLeads ? 'count leads' : 'report conversions';

  if (conversionIds.length > 0) {
    const count = conversionIds.length;
    return {
      state: 'ready',
      sentence: `This campaign will ${subject}: ${count} conversion rule${count === 1 ? '' : 's'} will be attached when it is created.`,
    };
  }
  if (rules.length > 0 && !rules.some((rule) => rule.enabled === true)) {
    return {
      state: 'rules_disabled',
      sentence: `This campaign cannot ${subject}: every conversion rule on the ad account is disabled. Enable one in LinkedIn Campaign Manager.`,
    };
  }
  return {
    state: 'no_rule',
    sentence: `This campaign cannot ${subject}: the ad account has no conversion rule to attach. Create one in LinkedIn Campaign Manager.`,
  };
}
